"use client";

import React, { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  Smartphone,
  User,
  Send,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/Select";
import { Checkbox } from "@/components/ui/checkbox";
import { Modal } from "@/components/ui/Modal";
import { useModal } from "@/lib/hooks/useModal";
import { images } from "@/lib/utils/images";
import { BranchCard, type Branch } from "@/components/ui/BranchCard";
import { useForm, Controller } from "react-hook-form";
import { useApi } from "@/lib/hooks/useApi";
import Input from "@/components/ui/Input";

const WhatsappIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={`shrink-0 transition-colors duration-200 ${props.className || ""}`}
    style={{
      width: props.width || "24px",
      height: props.height || "24px",
      ...props.style,
    }}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.447-.52.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.572-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

interface ContactFormData {
  targetArea: string;
  fullName: string;
  phone: string;
  mobilePhone: string;
  email: string;
  comment: string;
  privacyAccepted: boolean;
}

interface ContactNumberEntry {
  name?: string;
  phone?: string;
}

interface ContactFormProps {
  contactBranches: Branch[];
  contactNumbers: ContactNumberEntry[];
  contactEmail: string;
}

export default function ContactForm({
  contactBranches,
  contactNumbers,
  contactEmail,
}: ContactFormProps) {
  const { apiRequest } = useApi("");
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    defaultValues: {
      targetArea: "Atención y/o servicio ADISES",
      fullName: "",
      phone: "",
      mobilePhone: "",
      email: "",
      comment: "",
      privacyAccepted: false,
    },
  });

  // Branch location maps modal state
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [activeMapBranch, setActiveMapBranch] = useState<Branch>(contactBranches[0]);

  const { showModal, closeTopModal } = useModal();

  // Handle form submission
  const onSubmit = async (data: ContactFormData) => {
    try {
      apiRequest<{
        id?: string;
        data?: { id?: string; data?: { id?: string } };
        requestId?: string;
      }>("", {
        method: "post",
        url: `${process.env.NEXT_PUBLIC_API_URL}/business-operations/service-feedback-requests`,
        showToast: false,
        loadingMessage: "Enviando...",
        data,
      }).then((res) => {
          reset();
          const resData = res?.data ?? res;
          const requestId =
            resData?.id || resData?.data?.id || res?.requestId;

        showModal({
          title: "Solicitud enviada",
          size: "md",
          content: (
            <div className="p-4 text-center space-y-4">
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 size={28} />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-lg">
                  ¡Gracias por escribirnos!
                </h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Hemos recibido tu mensaje de contacto. Nuestro equipo se
                  pondrá en contacto contigo a la brevedad.
                </p>
              </div>

              {requestId && (
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/80 inline-block w-full max-w-sm">
                  <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    ID de Solicitud:
                  </p>
                  <p className="font-mono text-xs font-bold text-primary break-all mt-0.5 select-all">
                    {requestId}
                  </p>
                </div>
              )}

              <div className="pt-2 flex justify-center">
                <Button onClick={closeTopModal} className="px-6 font-semibold">
                  Aceptar
                </Button>
              </div>
            </div>
          ),
        });
      });
    } catch (err) {
      console.error(err);
    }
  };

  const iconsContainer =
    "p-2 text-primary rounded-xl shrink-0 mt-0.5 w-15 h-15";

  return (
    <>
      {/* SECTION 2: SUGGESTIONS & FEEDBACK FORM */}
      <section className="w-full py-8 bg-white flex justify-center border-b border-slate-100">
        <div className="w-full max-w-[1440px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Left Column: Context Info */}
          <div className="lg:col-span-4 flex flex-col justify-start space-y-4 text-left">
            <div className="space-y-3">
              <span className="text-md font-semibold tracking-widest text-primary">
                Servicio a clientes
              </span>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-800 tracking-tight leading-tight">
                ¿Tienes una sugerencia o necesitas ayuda?
              </h2>
              <p className="text-slate-500 text-xs md:text-sm font-sans font-semibold leading-relaxed">
                Estamos aquí para escucharte. Completa el formulario y nuestro
                equipo te atenderá a la brevedad.
              </p>
            </div>

            {/* Stepper-like benefits list */}
            <div className="space-y-2 mt-2">
              <div className="flex items-center gap-3">
                <div className={`${iconsContainer}`}>
                  <img
                    src={images.headPhoneIcon}
                    alt="Servicio"
                    className="w-full h-full object-contain hover:opacity-100 transition-all"
                  />
                </div>
                <div className="space-y-0.5">
                  <h4 className="font-extrabold text-slate-800 text-md">
                    Te escuchamos
                  </h4>
                  <p className="text-[12px] text-slate-500 font-medium font-sans">
                    Atendemos tus consultas, sugerencias y comentarios.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className={`${iconsContainer}`}>
                  <img
                    src={images.integrity}
                    alt="Respuesta ágil"
                    className="w-full h-full object-contain hover:opacity-100 transition-all"
                  />{" "}
                </div>
                <div className="space-y-0.5">
                  <h4 className="font-extrabold text-slate-800 text-md">
                    Respuesta ágil
                  </h4>
                  <p className="text-[12px] text-slate-500 font-medium font-sans">
                    Nos comprometemos a responder lo antes posible.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className={`${iconsContainer}`}>
                  <img
                    src={images.humanIcon}
                    alt="Atención"
                    className="w-full h-full object-contain hover:opacity-100 transition-all"
                  />
                </div>
                <div className="space-y-0.5">
                  <h4 className="font-extrabold text-slate-800 text-md">
                    Atención personalizada
                  </h4>
                  <p className="text-[12px] text-slate-500 font-medium font-sans">
                    Recibe el acompañamiento que necesitas.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className={`${iconsContainer}`}>
                  <img
                    src={images.lockedIcon}
                    alt="Confidencialidad"
                    className="w-full h-full object-contain hover:opacity-100 transition-all"
                  />
                </div>
                <div className="space-y-0.5">
                  <h4 className="font-extrabold text-slate-800 text-md">
                    Confidencialidad
                  </h4>
                  <p className="text-[12px] text-slate-500 font-medium font-sans">
                    Tu información está segura con nosotros.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Suggestions form */}
          <div className="lg:col-span-8 md:p-4">
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-4">
                  <Controller
                    name="targetArea"
                    control={control}
                    rules={{ required: "La sección de destino es requerida" }}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        label="¿Para quién es su sugerencia o comentario?*"
                        options={[
                          {
                            value: "Atención y/o servicio ADISES",
                            label: "Atención y/o servicio ADISES",
                          },
                          { value: "Soporte Técnico", label: "Soporte Técnico" },
                          { value: "Ventas y Cotizaciones", label: "Ventas y Cotizaciones" },
                          { value: "Facturación y Cobranza", label: "Facturación y Cobranza" },
                          { value: "Sugerencias generales", label: "Sugerencias generales" },
                        ]}
                        value={value}
                        onChange={onChange}
                        style="outlined"
                        error={errors.targetArea?.message}
                      />
                    )}
                  />

                  <Input
                    label="Nombre completo*"
                    placeholder="Ingresa tu nombre completo"
                    {...register("fullName", { required: "El nombre completo es requerido." })}
                    error={errors.fullName?.message}
                    variant="outline"
                    className="bg-white text-slate-700"
                    icon={<User className="w-4 h-4 text-slate-400" />}
                  />

                  <Input
                    label="Correo electrónico*"
                    placeholder="Ingresa tu correo electrónico"
                    type="email"
                    {...register("email", {
                      required: "El correo electrónico es requerido.",
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: "El correo electrónico no es válido.",
                      },
                    })}
                    error={errors.email?.message}
                    variant="outline"
                    className="bg-white text-slate-700"
                    icon={<Mail className="w-4 h-4 text-slate-400" />}
                  />
                </div>

                <div className="flex flex-col gap-4">
                  <Input
                    label="Teléfono*"
                    placeholder="Ingresa tu teléfono"
                    type="tel"
                    {...register("phone", { required: "El teléfono es requerido." })}
                    error={errors.phone?.message}
                    variant="outline"
                    className="bg-white text-slate-700"
                    icon={<Phone className="w-4 h-4 text-slate-400" />}
                  />

                  <Input
                    label="Celular*"
                    placeholder="Ingresa tu celular"
                    type="tel"
                    {...register("mobilePhone", { required: "El celular es requerido." })}
                    error={errors.mobilePhone?.message}
                    variant="outline"
                    className="bg-white text-slate-700"
                    icon={<Smartphone className="w-4 h-4 text-slate-400" />}
                  />
                </div>
              </div>

              <div className="w-full mt-4">
                <Input
                  label="Comentarios*"
                  placeholder="Escribe tu comentario..."
                  type="textArea"
                  {...register("comment", { required: "Los comentarios son requeridos." })}
                  error={errors.comment?.message}
                  variant="outline"
                  className="bg-white text-slate-700 min-h-[100px]"
                  rows={3}
                />
              </div>
              <div className="w-full flex flex-col gap-1 pt-3">
                <div className="flex items-center gap-2">
                  <Controller
                    name="privacyAccepted"
                    control={control}
                    rules={{ required: "Debe aceptar el aviso de privacidad." }}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        id="privacy-checkbox-contact"
                        checked={value}
                        onCheckedChange={onChange}
                      />
                    )}
                  />
                  <label
                    htmlFor="privacy-checkbox-contact"
                    className="text-xs text-slate-700 cursor-pointer select-none"
                  >
                    Acepto{" "}
                    <a
                      href="/aviso-de-privacidad"
                      className="text-primary hover:text-primary underline"
                      target="_blank"
                    >
                      aviso de privacidad
                    </a>
                  </label>
                </div>
                {errors.privacyAccepted && (
                  <p className="text-red-500 text-[10px] font-bold mt-0.5">
                    {errors.privacyAccepted.message}
                  </p>
                )}
              </div>
              <div className="flex justify-center mt-3">
                <Button type="submit" disabled={isSubmitting} className="w-full">
                  {isSubmitting ? (
                    "Enviando..."
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5 mr-1" />
                      Enviar
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* SECTION 3: CHANNELS MEDIA */}
      <section className="w-full py-4 flex justify-center border-b border-slate-100">
        <div className="w-full max-w-[1440px] px-6 md:px-12 space-y-6 flex flex-col items-center">
          <div className="text-center space-y-1">
            <h2 className="landing-title">Nuestros medios de contacto</h2>
            <p className="text-slate-500 text-xs md:text-sm font-sans font-medium">
              Elige el canal que prefieras. Estamos disponibles para atenderte.
            </p>
          </div>

          <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* WhatsApp Card */}
            <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-xs flex flex-col items-center text-center justify-between min-h-[200px] hover:shadow-md transition-shadow duration-300">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <WhatsappIcon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-extrabold text-slate-800 text-sm md:text-base">
                  Asesoría técnica <br /> vía WhatsApp
                </h3>
                <p className="text-[11px] text-slate-500 font-semibold leading-relaxed font-sans max-w-[200px]">
                  Escríbenos por WhatsApp y te ayudamos.
                </p>
              </div>
              <div className="flex flex-col gap-2 mt-4 w-full">
                {contactNumbers.map((item: ContactNumberEntry, idx: number) => (
                  <a
                    key={idx}
                    href={`https://wa.me/${item.phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full group"
                  >
                    <Button
                      variant="outline"
                      className="w-full gap-1.5 group-hover:bg-primary group-hover:text-white transition-colors duration-200 text-xs sm:text-sm py-2 h-auto min-h-9"
                    >
                      <WhatsappIcon className="w-3.5 h-3.5 text-primary group-hover:text-white transition-colors duration-200 shrink-0" />
                      <span className="break-all whitespace-normal text-center">{item.name}</span>
                    </Button>
                  </a>
                ))}
              </div>
            </div>

            {/* Email Support Card */}
            <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-xs flex flex-col items-center text-center justify-between min-h-[200px] hover:shadow-md transition-shadow duration-300">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Mail className="w-6 h-6" />
                </div>
                <h3 className="font-extrabold text-slate-800 text-sm md:text-base">
                  Soporte técnico <br /> vía e-mail
                </h3>
                <p className="text-[11px] text-slate-500 font-semibold leading-relaxed font-sans max-w-[200px]">
                  Envíanos tu solicitud y te responderemos.
                </p>
              </div>
              <div className="mt-4 w-full flex justify-center">
                <a href={`mailto:${contactEmail}`} className="w-full">
                  <Button variant="outline" className="w-full">
                    <Mail className="w-3.5 h-3.5" />
                    Enviar correo
                  </Button>
                </a>
              </div>
            </div>

            {/* Maps / Locations Card */}
            <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-xs flex flex-col items-center text-center justify-between min-h-[200px] hover:shadow-md transition-shadow duration-300">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <MapPin className="w-6 h-6" />
                </div>
                <h3 className="font-extrabold text-slate-800 text-sm md:text-base">
                  Mapas de <br /> sucursales
                </h3>
                <p className="text-[11px] text-slate-500 font-semibold leading-relaxed font-sans max-w-[200px]">
                  Encuentra la sucursal más cercana a ti.
                </p>
              </div>
              <div className="mt-4 w-full flex justify-center">
                <Button onClick={() => setIsMapModalOpen(true)} variant="outline" className="w-full">
                  <MapPin className="w-3.5 h-3.5" />
                  Ver ubicación
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: BRANCH OFFICES DIRECTORY */}
      <section className="w-full py-4 bg-white flex justify-center">
        <div className="w-full max-w-[1440px] px-6 md:px-12 space-y-6 flex flex-col items-center">
          <div className="text-center space-y-1">
            <h2 className="text-xl md:text-2xl font-semibold text-slate-800 tracking-tight">
              Asesoría técnica telefónica
            </h2>
            <p className="text-slate-500 text-xs md:text-sm font-sans font-medium">
              Comunícate a la sucursal más cercana y recibe atención especializada.
            </p>
          </div>

          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {contactBranches.map((branch: Branch, index: number) => (
              <BranchCard
                key={index}
                branch={branch}
                onClick={() => {
                  setActiveMapBranch(branch);
                  setIsMapModalOpen(true);
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* SUCURSAL BRANCH MAPS TABBED MODAL */}
      {isMapModalOpen && (
        <Modal
          isOpen={isMapModalOpen}
          onClose={() => setIsMapModalOpen(false)}
          title="Ubicación de Sucursales ADISES"
          size="xl"
        >
          <div className="flex flex-col h-full max-h-[85vh]">
            <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-thin border-b border-gray-100">
              {contactBranches.map((branch: Branch, idx: number) => (
                <button
                  key={idx}
                  onClick={() => setActiveMapBranch(branch)}
                  className={`py-2 px-4 rounded-full font-bold text-xs shrink-0 transition-all ${
                    activeMapBranch.name === branch.name
                      ? "bg-[#135eaa] text-white shadow-sm"
                      : "bg-slate-100 hover:bg-slate-200 text-[#083459]"
                  }`}
                >
                  {branch.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 flex-1 min-h-0 overflow-y-auto">
              <div className="lg:col-span-4 bg-slate-50 p-5 flex flex-col justify-between">
                <div>
                  <h3 className="font-bold text-base text-[#083459] mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#1894bc]" />
                    ADISES {activeMapBranch.name}
                  </h3>

                  <p className="text-xs text-gray-600 mb-5 leading-relaxed">
                    {activeMapBranch.address}
                  </p>

                  <div className="space-y-2.5">
                    <p className="text-xs font-bold text-[#083459] mb-1">
                      Teléfonos de contacto:
                    </p>
                    {activeMapBranch.phones.map((phone, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-xs font-semibold text-[#135eaa]"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        <span>{phone}</span>
                      </div>
                    ))}
                    {activeMapBranch.extensions && (
                      <p className="text-[10px] text-primary font-semibold mt-1">
                        OFICINA: {activeMapBranch.extensions}
                      </p>
                    )}
                  </div>
                </div>

                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    activeMapBranch.mapQuery
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 flex items-center justify-center gap-2 bg-[#083459] hover:bg-[#135eaa] text-white py-3 px-4 rounded-xl font-bold text-xs transition-colors shadow-xs"
                >
                  Ver en Google Maps
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>

              <div className="lg:col-span-8 h-[360px] md:h-[420px] rounded-2xl overflow-hidden border border-slate-200 shadow-xs relative bg-slate-100">
                <iframe
                  title={`Mapa Sucursal ADISES ${activeMapBranch.name}`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(
                    activeMapBranch.mapQuery
                  )}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
